import {Component, computed, inject, input, output, signal} from '@angular/core';
import {CardModule} from 'primeng/card';
import {ButtonModule} from 'primeng/button';
import {TranslocoDirective} from '@jsverse/transloco';
import {
  ProviderTenderControllerService,
  ProviderUserControllerService,
  ProviderUserResponse,
} from '@frontend/shared-api';
import {ToastService} from '@frontend/toast';
import {FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {FormFieldComponent} from '../../../../../shared/components/inputs/form-field/form-field.component';
import {
  AbstractFormDirective,
  FormControlsConfig,
} from '../../../../customer/project-sequence/chapter/chapter-8-rfq/step-8-1-proposal-parameter/components/abstract-form-directive';
import {rxResource} from '@angular/core/rxjs-interop';
import {LoadingComponent} from '../../../../../shared/components/loading/loading.component';
import {LoadingSize} from '../../../../../shared/components/loading/loading.types';
import {TenderCardContactPersonFormData} from './tender-card-contact-select-form.types';

@Component({
  selector: 'app-tender-card-contact-select-form',
  imports: [
    CardModule,
    ButtonModule,
    TranslocoDirective,
    FormsModule,
    FormFieldComponent,
    ReactiveFormsModule,
    LoadingComponent,
  ],
  templateUrl: './tender-card-contact-select-form.component.html',
})
export class TenderCardSelectFormComponent extends AbstractFormDirective<TenderCardContactPersonFormData> {
  protected readonly LoadingSize = LoadingSize;

  private static readonly FORM_CONTROLS: FormControlsConfig<TenderCardContactPersonFormData> = {
    tenderContactPersonUuid: [null, {validators: [Validators.required]}],
  };
  private readonly providerUserService = inject(ProviderUserControllerService);
  private readonly providerTenderService = inject(ProviderTenderControllerService);
  private readonly toast = inject(ToastService);

  readonly tenderId = input.required<number>();
  readonly contactPersonSaved = output<string>();

  constructor() {
    super(TenderCardSelectFormComponent.FORM_CONTROLS);
  }

  readonly editMode = signal(false);

  readonly providerContactsResource = rxResource({
    stream: () => this.providerUserService.getAllActiveProviderUsers(),
  });

  readonly providerContacts = computed(
    () => this.providerContactsResource.value() ?? ([] as ProviderUserResponse[]),
  );

  readonly providerContactOptions = computed(() =>
    this.providerContacts().map((contact) => ({
      label: this.getFullName(contact),
      value: contact.uuid,
    })),
  );

  readonly selectedContactPersonName = computed(() => {
    const tenderContactPersonUuid =
      this.formValueSignal().tenderContactPersonUuid ?? this.inputObject()?.tenderContactPersonUuid;

    if (!tenderContactPersonUuid) return null;

    const contact = this.providerContacts().find((c) => c.uuid === tenderContactPersonUuid);
    if (!contact) return null;

    return this.getFullName(contact);
  });

  override onSubmit(): void {
    const tenderContactPersonUuid = this.theForm.getRawValue().tenderContactPersonUuid;
    if (!tenderContactPersonUuid) {
      this.toast.showError('common.error', 'error.generic.detail');
      return;
    }
    this.providerTenderService
      .updateTenderContactPerson({tenderId: this.tenderId(), body: tenderContactPersonUuid})
      .subscribe({
        next: () => {
          this.editMode.set(false);
          this.contactPersonSaved.emit(tenderContactPersonUuid);
        },
        error: () => this.editMode.set(false),
      });
  }

  private getFullName(contact: ProviderUserResponse): string {
    return [contact.firstName, contact.lastName].filter(Boolean).join(' ');
  }
}
